import { SxProps, Theme } from '@mui/material';

/**
 * Shared chrome for the Share & Schedule dialog and its three tabs. Plain `sx`
 * constants rather than styled-components, matching the HEADER_SX / CELL_SX
 * idiom used by the My Reports table these tabs sit on top of.
 */

/**
 * 880px rather than the column picker's 1240 — this is a single-column form, and
 * a wider paper just stretches the fields. The paper is a flex column so the
 * header and footer stay put while only the tab body scrolls.
 */
export const DIALOG_PAPER_SX: SxProps<Theme> = {
  width: { xs: '100%', sm: 'min(880px, calc(100vw - 48px))' },
  maxWidth: 'none',
  height: { xs: '100%', sm: 'auto' },
  minHeight: { xs: '100%', sm: 420 },
  maxHeight: { xs: '100%', sm: 'calc(100vh - 48px)' },
  borderRadius: { xs: 0, sm: 2 },
  bgcolor: 'v4.surface',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

export const TABS_SX: SxProps<Theme> = { minHeight: 40, px: 1 };

/**
 * Plain object, not `SxProps` — the Tabs molecule types `tabSx` as `object`.
 *
 * `v4.textMuted` rather than `text.secondary` for the same reason every muted
 * *text* colour in this dialog uses it: the theme leaves `text.secondary` at MUI's
 * default `rgba(0,0,0,.6)`, which is 4.6:1 on white — over AA, but a thin margin to
 * spend on 13px labels. `textMuted` is ~7.4:1. Icons stay on `text.secondary`,
 * where the bar is only 3:1.
 */
export const TAB_SX = {
  textTransform: 'none',
  backgroundColor: 'transparent',
  minHeight: 40,
  py: 0.75,
  px: 2,
  fontSize: '13px',
  fontWeight: 500,
  color: 'v4.textMuted',
  '&.Mui-selected': { color: 'v4.accentDark', fontWeight: 600 },
};

/** The dialog owns the only scroll region; tabs must not add their own. */
export const DIALOG_BODY_SX: SxProps<Theme> = { flex: 1, minHeight: 0, overflowY: 'auto' };

/** Outer padding for a tab's form content. Capped so fields stay readable. */
export const TAB_BODY_SX: SxProps<Theme> = { px: 2, py: 2 };

export const FORM_STACK_SX: SxProps<Theme> = { maxWidth: 720 };

export const SECTION_HEADING_SX: SxProps<Theme> = {
  fontSize: '11px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'v4.textMuted',
};

export const FIELD_HINT_SX: SxProps<Theme> = {
  color: 'v4.textMuted',
  mt: 0.5,
  display: 'block',
};
